/**
 * Project Management API
 * Centralised axios calls for projects, tasks, and attachments.
 * All requests go through the existing axios instance which handles
 * the JWT auth header automatically.
 */

import axios from 'axios';

const BASE = '/project-management';

// ── Projects ──────────────────────────────────────────────────────────────────

export const fetchProjects = (params = {}) =>
    axios.get(`${BASE}/projects`, { params });

export const fetchProjectById = (id) =>
    axios.get(`${BASE}/projects/${id}`);

export const createProject = (data) =>
    axios.post(`${BASE}/projects`, data);

export const updateProject = (id, data) =>
    axios.put(`${BASE}/projects/${id}`, data);

export const deleteProject = (id) =>
    axios.delete(`${BASE}/projects/${id}`);

export const syncChecklistFeedback = (requestId, data) =>
    axios.post(`${BASE}/project-requests/${requestId || 'sync'}/sync-checklist`, data);

export const rejectProjectRequest = (requestId, data) =>
    axios.post(`${BASE}/project-requests/${requestId || 'reject'}/reject`, data);

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const fetchTasks = (params = {}) =>
    axios.get(`${BASE}/tasks`, { params });

export const fetchTaskSummary = (params = {}) =>
    axios.get(`${BASE}/tasks/summary`, { params });

// [PM-MIGRATION] Employee-facing — tasks assigned to the logged-in user (Backlog + In Progress)
export const fetchMyTasks = () =>
    axios.get(`${BASE}/tasks/my-tasks`);

export const fetchTaskById = (id) =>
    axios.get(`${BASE}/tasks/${id}`);

export const createTask = (data) =>
    axios.post(`${BASE}/tasks`, data);

export const updateTask = (id, data) =>
    axios.put(`${BASE}/tasks/${id}`, data);
// data may include actualTimeMs (number, ms) for manual effort override from EditTaskModal

export const updateTaskStatus = (id, status, note = '', completionComments = '', extra = {}) => {
    const payload = typeof extra === 'object' ? { status, note, completionComments, ...extra } : { status, note, completionComments };
    return axios.patch(`${BASE}/tasks/${id}/status`, payload);
};

export const convertWorkItemType = (id) =>
    axios.post(`${BASE}/tasks/${id}/convert-type`);

export const deleteTask = (id) =>
    axios.delete(`${BASE}/tasks/${id}`);

export const revokeShareLink = (id) =>
    axios.delete(`${BASE}/tasks/${id}/share`);

export const getSharedTask = (token) =>
    axios.get(`${BASE}/tasks/shared/${token}`);

// ── Attachments ───────────────────────────────────────────────────────────────

export const uploadAttachment = (taskId, formData, config = {}) =>
    axios.post(`${BASE}/tasks/${taskId}/attachments`, formData, {
        ...config,
        headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) },
    });

export const fetchAttachments = (taskId) =>
    axios.get(`${BASE}/tasks/${taskId}/attachments`);

export const getDownloadUrl = (taskId, attachmentId, disposition = 'inline') =>
    axios.get(`${BASE}/tasks/${taskId}/attachments/${attachmentId}/download`, {
        params: { disposition },
    });

export const deleteAttachment = (taskId, attachmentId) =>
    axios.delete(`${BASE}/tasks/${taskId}/attachments/${attachmentId}`);

// ── Comments [PM-MIGRATION] ───────────────────────────────────────────────────

export const addComment = (taskId, textOrPayload, maybeFileRefs = []) => {
    const payload = typeof textOrPayload === 'object'
        ? textOrPayload
        : { text: textOrPayload, fileReferences: maybeFileRefs };
    return axios.post(`${BASE}/tasks/${taskId}/comments`, payload);
};

export const deleteComment = (taskId, commentId) =>
    axios.delete(`${BASE}/tasks/${taskId}/comments/${commentId}`);

// ── Subtasks [task-drawer-revamp] ─────────────────────────────────────────────

export const addSubtask = (taskId, title) =>
    axios.post(`${BASE}/tasks/${taskId}/subtasks`, { title });

export const updateSubtask = (taskId, subId, data) =>
    axios.patch(`${BASE}/tasks/${taskId}/subtasks/${subId}`, data);

export const deleteSubtask = (taskId, subId) =>
    axios.delete(`${BASE}/tasks/${taskId}/subtasks/${subId}`);

// ── Task Sharing [task-drawer-revamp] ─────────────────────────────────────────

// Generates/regenerates a share token (authenticated — returns { token, expiresAt })
export const generateShareLink = (taskId) =>
    axios.post(`${BASE}/tasks/${taskId}/share`);

// Public endpoint — no auth header required.
// Uses the same axios instance (JWT header won't hurt unauthenticated routes).
export const fetchSharedTask = (token) =>
    axios.get(`${BASE}/tasks/shared/${token}`);

export const fetchSharedTaskAiSummary = (token, force = false) =>
    axios.get(`${BASE}/tasks/shared/${token}/ai-summary`, { params: force ? { force: true } : {} });

// ── AI Summary [task-drawer-revamp] ─────────────────────────────────────────
// Sends the full task context to OpenAI and returns a structured HTML summary.
export const fetchTaskAiSummary = (taskId, force = false) =>
    axios.get(`${BASE}/tasks/${taskId}/ai-summary`, { params: force ? { force: true } : {} });

// ── AI Improve Description [task-drawer-revamp] ──────────────────────────────
// Sends the current (possibly unsaved) description to AI and returns an improved version.
// Pass the live editDescription so in-flight edits are used, not just the stored value.
export const improveTaskDescription = (taskId, description) =>
    axios.post(`${BASE}/tasks/${taskId}/ai-improve-description`, { description });

// ── Sprint Report AI Summary ──────────────────────────────────────────────────
// Sends the current task list to the backend, which computes all metrics and
// calls AI to generate the executive summary. Returns a full report payload.
export const generateSprintReport = (tasks) =>
    axios.post(`${BASE}/sprint-report/ai-summary`, { tasks });

export const fetchSprintReports = (params = {}) =>
    axios.get(`${BASE}/sprint-reports`, { params });

export const saveSprintReport = (data) =>
    axios.post(`${BASE}/sprint-reports`, data);

export const markReportAsRead = (id) =>
    axios.patch(`${BASE}/sprint-reports/${id}/read`);

export const shareSprintReport = (id, channel) =>
    axios.post(`${BASE}/sprint-reports/${id}/share`, { channel });


// ── Sprint CRUD ───────────────────────────────────────────────────────────────
export const fetchSprints = (params = {}) =>
    axios.get(`${BASE}/sprints`, { params });

export const fetchSprintById = (id) =>
    axios.get(`${BASE}/sprints/${id}`);

export const createSprint = (data) =>
    axios.post(`${BASE}/sprints`, data);

export const updateSprint = (id, data) =>
    axios.put(`${BASE}/sprints/${id}`, data);

export const deleteSprint = (id) =>
    axios.delete(`${BASE}/sprints/${id}`);

export const closeSprint = (id) =>
    axios.post(`${BASE}/sprints/${id}/close`, {});

// ── Timer State [task-drawer-revamp] ─────────────────────────────────────────
// Checks whether any employee is currently running this PM task in their attendance sheet.
// Called on drawer open so the timer/button restores correctly without needing a socket event.
export const fetchTaskTimerState = (taskId) =>
    axios.get(`${BASE}/tasks/${taskId}/timer-state`);

export const fetchEmployeeNames = () =>
    axios.get('/employeesNames');

// ── AI Deliverable Revision Review ───────────────────────────────────────────
// Sends file metadata + taskId to the backend for AI revision criteria evaluation.
// Returns { status, summary, criteria, satisfiedItems, missingItems, confidence }
export const reviewDeliveredFile = (payload) =>
    axios.post(`${BASE}/ai/review-delivered-file`, payload);
